
import { useEffect, useRef, useState } from 'react';

export default function IndustryGrid() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Guard against browsers that do not support IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    let observer: IntersectionObserver | null = null;

    try {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Once visible we can stop observing
            observer?.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }
    } catch (err) {
      // Fallback – show the content if the observer fails to initialise
      console.error('IntersectionObserver error:', err);
      setIsVisible(true);
    }

    // Cleanup
    return () => {
      observer?.disconnect();
    };
  }, []);

  const industries = [
    {
      title: 'Healthcare',
      subtitle: 'GLP-1 Clinics, Weight Loss Centers, Med Spas',
      description:
        'Automated patient intake, eligibility screening, scheduling intelligence.',
      icon: 'ri-heart-pulse-line',
      image:
        'https://public.readdy.ai/ai/img_res/87b155e417dbd2afae6744bb59895c79.jpg',
    },
    {
      title: 'Legal',
      subtitle: 'Immigration & Personal Injury Firms',
      description:
        'Case intake triage, document workflow automation, client communication systems.',
      icon: 'ri-scales-3-line',
      image:
        'https://readdy.ai/api/search-image?query=Elegant%20modern%20law%20firm%20office%20interior%20with%20dark%20wood%20accents%20and%20contemporary%20furniture%20showing%20a%20professional%20conference%20room%20with%20large%20windows%20and%20city%20view%20premium%20corporate%20legal%20environment%20sophisticated%20minimalist%20design%20warm%20lighting%20executive%20atmosphere&width=640&height=400&seq=industry-legal-1&orientation=landscape',
    },
    {
      title: 'Home Services',
      subtitle: 'HVAC & Roofing Operators',
      description:
        'Speed-to-lead automation, dispatch coordination, revenue tracking.',
      icon: 'ri-home-gear-line',
      image:
        'https://readdy.ai/api/search-image?query=Professional%20home%20service%20technician%20using%20a%20modern%20tablet%20device%20for%20digital%20dispatch%20and%20scheduling%20in%20a%20clean%20organized%20service%20van%20with%20tools%20neatly%20arranged%20premium%20commercial%20photography%20style%20warm%20natural%20lighting%20professional%20trade%20services%20aesthetic&width=640&height=400&seq=industry-home-1&orientation=landscape',
    },
  ];

  return (
    <section
      id="industries"
      ref={sectionRef}
      className="py-28 bg-[#F7F9FC]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            className="text-3xl md:text-4xl font-semibold text-[#0E1A2B] mb-4"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
          >
            Industry Applications
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Purpose-built AI systems tailored to the unique demands of each
            sector.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {industries.map((industry, index) => (
            <div
              key={index}
              className={`group bg-white rounded-2xl overflow-hidden border border-[#DDE3EA] hover:shadow-xl hover:-translate-y-2 transition-all duration-500 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              {/* Card Image */}
              <div className="w-full h-52 overflow-hidden relative">
                <img
                  src={industry.image}
                  alt={`${industry.title} AI solutions`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                  <i
                    className={`${industry.icon} text-lg text-[#2FB8AC]`}
                    aria-hidden="true"
                  ></i>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-7">
                <h3
                  className="text-xl font-semibold text-[#0E1A2B] mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {industry.title}
                </h3>
                <p className="text-xs font-medium text-[#4A6FA5] mb-3 uppercase tracking-wide">
                  {industry.subtitle}
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {industry.description}
                </p>
                <div className="mt-5 flex items-center text-[#2FB8AC] text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                  <span>Learn more</span>
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
